import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader } from "../components/layout/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, PencilSimple, Trash, Check, Prohibit, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATS  = ["build", "enhance", "integrate", "refactor", "automate", "content"];
const PRIOS = ["P0", "P1", "P2", "P3"];
const STATUSES = ["open", "in_progress", "done", "dismissed"];

const PRIO_COLOR = {
  P0: "text-red-700 bg-red-50 border-red-200",
  P1: "text-amber-700 bg-amber-50 border-amber-200",
  P2: "text-blue-700 bg-blue-50 border-blue-200",
  P3: "text-slate-600 bg-slate-100 border-slate-200",
};
const STATUS_COLOR = {
  open:        "text-slate-800 bg-slate-100 border-slate-300",
  in_progress: "text-violet-700 bg-violet-50 border-violet-200",
  done:        "text-emerald-700 bg-emerald-50 border-emerald-200",
  dismissed:   "text-slate-500 bg-slate-50 border-slate-200",
};

function initForm() {
  return { title: "", description: "", category: "enhance", priority: "P2", status: "open", source: "user" };
}

export default function Backlog() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_status: {}, by_priority: {} });
  const [filterStatus, setFilterStatus] = useState("open");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initForm());

  const load = async () => {
    const params = filterStatus === "all" ? {} : { status: filterStatus };
    const [r, s] = await Promise.all([
      api.get("/suggestions", { params }),
      api.get("/suggestions/summary"),
    ]);
    setRows(r.data);
    setSummary(s.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus]);

  const openNew = () => { setEditing(null); setForm(initForm()); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...initForm(), ...s }); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      if (editing) await api.put(`/suggestions/${editing.id}`, form);
      else await api.post("/suggestions", form);
      toast.success(editing ? "Updated" : "Added to backlog");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const setStatus = async (s, status) => {
    await api.put(`/suggestions/${s.id}`, { ...s, status });
    toast.success(`Moved to ${status}`);
    load();
  };

  const del = async (s) => {
    if (!confirm(`Delete "${s.title}"?`)) return;
    await api.delete(`/suggestions/${s.id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="backlog-page">
      <PageHeader
        title="Backlog"
        subtitle="Everything E1 has suggested + your own ideas. Promote, dismiss, or edit."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="backlog-new-btn">
                <Plus size={14} className="mr-2" /> New Item
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="backlog-dialog">
              <DialogHeader><DialogTitle>{editing ? "Edit item" : "New backlog item"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="backlog-field-title" className="mt-1" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} className="mt-1" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIOS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
              </div>
              <DialogFooter><Button onClick={save} data-testid="backlog-save-btn">{editing ? "Save" : "Add"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Summary chips */}
      <div className="flex flex-wrap gap-2">
        {["all", ...STATUSES].map((s) => {
          const active = filterStatus === s;
          const count = s === "all" ? summary.total : (summary.by_status[s] || 0);
          return (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              data-testid={`backlog-filter-${s}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-300 hover:border-blue-400"}`}
            >
              {s === "all" ? "All" : s.replace("_", " ")}
              <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] ${active ? "bg-white/20" : "bg-slate-100 text-slate-600"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Title</th>
              <th className="text-left px-4 py-3 font-semibold">Category</th>
              <th className="text-left px-4 py-3 font-semibold">Priority</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-left px-4 py-3 font-semibold">Source</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No items in this view.</td></tr>
            )}
            {rows.map(s => (
              <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`backlog-row-${s.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{s.title}</div>
                  {s.description && <div className="text-xs text-slate-500 mt-0.5">{s.description}</div>}
                </td>
                <td className="px-4 py-3"><span className="text-[11px] bg-slate-100 text-slate-700 rounded px-2 py-0.5">{s.category}</span></td>
                <td className="px-4 py-3"><span className={`text-[11px] rounded border px-2 py-0.5 font-medium ${PRIO_COLOR[s.priority]}`}>{s.priority}</span></td>
                <td className="px-4 py-3"><span className={`text-[11px] rounded border px-2 py-0.5 ${STATUS_COLOR[s.status]}`}>{s.status.replace("_"," ")}</span></td>
                <td className="px-4 py-3">
                  {s.source === "e1" ? (
                    <span className="text-[11px] inline-flex items-center gap-1 text-violet-700 bg-violet-50 rounded px-2 py-0.5"><Sparkle size={10} /> E1</span>
                  ) : (
                    <span className="text-[11px] text-slate-500">{s.source}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-1 justify-end">
                    {s.status === "open" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setStatus(s, "in_progress")} data-testid={`backlog-start-${s.id}`} title="Start">
                          Start
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setStatus(s, "dismissed")} data-testid={`backlog-dismiss-${s.id}`} title="Dismiss" className="text-slate-500">
                          <Prohibit size={13} />
                        </Button>
                      </>
                    )}
                    {(s.status === "in_progress" || s.status === "open") && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(s, "done")} data-testid={`backlog-done-${s.id}`} title="Mark done" className="text-emerald-600">
                        <Check size={13} />
                      </Button>
                    )}
                    {(s.status === "done" || s.status === "dismissed") && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(s, "open")} data-testid={`backlog-reopen-${s.id}`}>
                        Reopen
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`backlog-edit-${s.id}`}><PencilSimple size={13} /></Button>
                    <Button size="sm" variant="outline" onClick={() => del(s)} className="text-red-600" data-testid={`backlog-del-${s.id}`}><Trash size={13} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
